import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@shared/ui';
import { useApiServices } from '@shared/lib/use-api-services';
import { formatDateTime } from '@shared/lib/format-date-time';
import type { WithdrawalPolicyDecisionDto } from '@shared/api/contracts';

const asList = (value: WithdrawalPolicyDecisionDto[] | { data: WithdrawalPolicyDecisionDto[] } | undefined) => {
  if (!value) return [];
  return Array.isArray(value) ? value : value.data ?? [];
};

export function PolicyDecisionsDialog({
  withdrawalId,
  onOpenChange,
}: {
  withdrawalId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation('cabinet');
  const services = useApiServices();
  const decisions = useSWR(withdrawalId ? ['policy-decisions', withdrawalId] : null, () =>
    services.withdrawals.getPolicyDecisions(withdrawalId ?? ''),
  );
  const rows = asList(decisions.data);

  return (
    <Dialog open={Boolean(withdrawalId)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('withdrawals.policyHistory.title')}</DialogTitle>
          <DialogDescription>{t('withdrawals.policyHistory.hint')}</DialogDescription>
        </DialogHeader>
        {decisions.isLoading ? <p className="text-sm text-muted-foreground">{t('withdrawals.policyHistory.loading')}</p> : null}
        {!decisions.isLoading && rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('withdrawals.policyHistory.empty')}</p>
        ) : (
          <ul className="space-y-3">
            {rows.map((item) => (
              <li key={item.id} className="rounded-lg border border-border p-3 text-sm">
                <p className="font-medium text-foreground">
                  {t(`withdrawals.policyHistory.phase.${item.phase}`, { defaultValue: item.phase })}
                </p>
                <p className="text-muted-foreground">{formatDateTime(item.createdAt, i18n.language)}</p>
                <p>{t(`withdrawals.policy.${item.outcome}`, { defaultValue: String(item.outcome) })}</p>
                {(item.checks ?? []).map((check) => (
                  <p key={check.name} className="text-xs text-muted-foreground">
                    {check.name}: {t(`withdrawals.checks.${check.result}`, { defaultValue: check.result })}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
